import type {
  BinaryOperator,
  TernaryOperator,
  UnaryOperator,
} from "../blueprint/types.js";
import { evaluateDateDifference } from "../blueprint/date-operators.js";
import {
  extendObject,
  selectObjectProperties,
  setObjectProperty,
  objectEntries,
} from "../blueprint/object-operators.js";
import {
  evaluateListAggregateGroupsBy,
  evaluateListAverage,
  evaluateListGroupBy,
  evaluateListMax,
  evaluateListMin,
  evaluateListSum,
  evaluateListTake,
  objectValueToString,
} from "../blueprint/method.js";
import { getLanguage } from "../language/dico.js";
import { resolvePath } from "../layout/resolution.js";
import {
  BinaryOperatorOptions,
  UnaryOperatorOptions,
  clamp,
  coerceToBoolean,
  evaluateListFind,
  evaluateListFindByPath,
  evaluateListFindByPathIndex,
  evaluateListFindIndex,
  evaluateObjectDefined,
  evaluateObjectEmpty,
  isBoxShadowString,
  isColorString,
  isFilterString,
  isGradientString,
  isTextShadowString,
  toBorderConfig,
  toBorderRadiusConfig,
  toColorMixRatio,
  toListValue,
  toObjectValue,
} from "./helpers.js";
import {
  normalizeFontFamilyValue,
  normalizeFontWeightValue,
  normalizeTextAlignValue,
  normalizeTextDecorationLineValue,
  normalizeTextDecorationStyleValue,
  normalizeTextTransformValue,
  normalizeWhiteSpaceValue,
} from "./normalize.js";

/**
 * Evaluates a unary operator (aligned with {@link OperatorBehaviorNode} runtime).
 */
export function evaluateUnaryOperator(
  operator: UnaryOperator,
  value: unknown,
  options?: UnaryOperatorOptions,
): unknown {
  if (operator === "objectKeys")
    return objectEntries(value).map((item) => item.key);
  if (operator === "objectValues")
    return objectEntries(value).map((item) => item.value);
  if (operator === "objectEntries") return objectEntries(value);
  const inputIsObject = options?.input1ValueType === "object";
  const locale = getLanguage();

  switch (operator) {
    case "defined":
      if (inputIsObject) {
        return evaluateObjectDefined(value);
      }
      return (
        value != null &&
        value !== "" &&
        !(typeof value === "number" && Number.isNaN(value))
      );

    case "empty":
      if (inputIsObject) {
        return evaluateObjectEmpty(value);
      }
      if (Array.isArray(value)) {
        return value.length === 0;
      }
      return (
        value == null ||
        value === "" ||
        (typeof value === "number" && Number.isNaN(value))
      );

    case "not":
      return !coerceToBoolean(value);

    case "length":
      return toListValue(value).length;

    case "isNotEmpty":
      return toListValue(value).length > 0;

    case "ceil":
      return Math.ceil(Number(value));
    case "sum":
      return evaluateListSum(value);

    case "average":
      return evaluateListAverage(value);

    case "min":
      return evaluateListMin(value);

    case "max":
      return evaluateListMax(value);

    case "factorial": {
      const n = Number(value);
      if (!Number.isFinite(n) || n < 0) return 0;
      let result = 1;
      for (let i = 2; i <= Math.floor(n); i++) result *= i;
      return result;
    }

    case "castToString":
      if (options?.input1ValueType === "object") {
        return objectValueToString(value);
      }
      return options?.input1ValueType === "date"
        ? new Date(String(value)).toLocaleDateString(locale)
        : String(value);

    case "castToNumber":
      return Number(value);

    case "castToFontFamily":
      return normalizeFontFamilyValue(value);

    case "castToFontWeight":
      return normalizeFontWeightValue(value);

    case "castToTextAlign":
      return normalizeTextAlignValue(value);

    case "castToTextTransform":
      return normalizeTextTransformValue(value);

    case "castToWhiteSpace":
      return normalizeWhiteSpaceValue(value);

    case "castToTextDecorationLine":
      return normalizeTextDecorationLineValue(value);

    case "castToTextDecorationStyle":
      return normalizeTextDecorationStyleValue(value);

    case "castToBoolean": {
      if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (normalized === "true" || normalized === "1") return true;
        if (normalized === "false" || normalized === "0" || normalized === "") {
          return false;
        }
      }
      return Boolean(value);
    }

    case "castToColor": {
      const candidate = String(value ?? "").trim();
      return isColorString(candidate) ? candidate : "#00000000";
    }

    case "castToGradient": {
      const candidate = String(value ?? "").trim();
      return isGradientString(candidate)
        ? candidate
        : "linear-gradient(90deg, #000000 0%, #ffffff 100%)";
    }

    case "castToFilter": {
      const candidate = String(value ?? "").trim();
      return isFilterString(candidate) ? candidate : "none";
    }

    case "castToBoxShadow": {
      const candidate = String(value ?? "").trim();
      return isBoxShadowString(candidate) ? candidate : "none";
    }

    case "castToTextShadow": {
      const candidate = String(value ?? "").trim();
      return isTextShadowString(candidate) ? candidate : "none";
    }

    case "castToBorder":
      return toBorderConfig(value);

    case "castToBorderRadius":
      return toBorderRadiusConfig(value);

    case "castToObject":
      return toObjectValue(value);

    case "toUpperCase":
      return String(value).toUpperCase();

    case "toLowerCase":
      return String(value).toLowerCase();

    case "capitalize":
      return String(value).charAt(0).toUpperCase() + String(value).slice(1);

    case "getDay": {
      const date = new Date(String(value));
      const df = options?.datePartTextFormat;
      if (!df || df === "numeric") {
        return date.getDay();
      }
      if (df === "narrow" || df === "short" || df === "long") {
        return date.toLocaleDateString(locale, { weekday: df });
      }
      return date.toLocaleDateString(locale, { day: df });
    }

    case "getMonth": {
      const date = new Date(String(value));
      const df = options?.datePartTextFormat;
      if (!df || df === "numeric") {
        return date.getMonth();
      }
      return date.toLocaleDateString(locale, { month: df });
    }

    case "getYear":
      return new Date(String(value)).getFullYear();

    case "getHour":
      return new Date(String(value)).getHours();

    case "getMinute":
      return new Date(String(value)).getMinutes();

    case "getSecond":
      return new Date(String(value)).getSeconds();

    case "currentEntry":
    case "currentItem":
    case "comparator":
      return undefined;

    default:
      return false;
  }
}

/**
 * Evaluates a binary operator (aligned with {@link OperatorBehaviorNode} runtime).
 */
export function evaluateBinaryOperator(
  operator: BinaryOperator,
  input1: unknown,
  input2: unknown,
  options?: BinaryOperatorOptions,
): unknown {
  if (operator === "extendObject") return extendObject(input1, input2);
  if (operator === "pickObject") return selectObjectProperties(input1, input2);
  if (operator === "omitObject")
    return selectObjectProperties(input1, input2, true);
  const locale = getLanguage();

  switch (operator) {
    case "dateDifference":
      return evaluateDateDifference(
        input1,
        input2,
        options?.dateDifferencePart,
      );
    case "and":
      return coerceToBoolean(input1) && coerceToBoolean(input2);
    case "or":
      return coerceToBoolean(input1) || coerceToBoolean(input2);

    case "greaterThan":
      return Number(input1) > Number(input2);
    case "lessThan":
      return Number(input1) < Number(input2);
    case "greaterThanOrEqual":
      return Number(input1) >= Number(input2);
    case "lessThanOrEqual":
      return Number(input1) <= Number(input2);

    case "contains":
      if (Array.isArray(input1)) {
        const serializedNeedle = String(input2 ?? "");
        return toListValue(input1).some(
          (item) =>
            String(item) === serializedNeedle ||
            JSON.stringify(item) === serializedNeedle,
        );
      }
      return String(input1 ?? "").includes(String(input2 ?? ""));

    case "notContains":
      return !String(input1 ?? "").includes(String(input2 ?? ""));

    case "startsWith":
      return String(input1 ?? "").startsWith(String(input2 ?? ""));

    case "endsWith":
      return String(input1 ?? "").endsWith(String(input2 ?? ""));

    case "add":
      return Number(input1) + Number(input2);
    case "subtract":
      return Number(input1) - Number(input2);
    case "multiply":
      return Number(input1) * Number(input2);
    case "divide": {
      const divisor = Number(input2);
      return divisor === 0 ? 0 : Number(input1) / divisor;
    }
    case "modulo": {
      const mod = Number(input2);
      return mod === 0 ? 0 : Number(input1) % mod;
    }
    case "power":
      return Math.pow(Number(input1), Number(input2));

    case "round": {
      const numValue = Number(input1);
      const rawDecimals = Number(input2);
      if (!Number.isFinite(numValue)) {
        return Number.NaN;
      }
      const decimals = Number.isFinite(rawDecimals)
        ? Math.max(0, Math.min(20, Math.floor(rawDecimals)))
        : 0;
      const factor = 10 ** decimals;
      return Math.round(numValue * factor) / factor;
    }

    case "concat":
      return String(input1 ?? "") + String(input2 ?? "");

    case "chartAt": {
      const source = String(input1 ?? "");
      const index = Number(input2);
      if (!Number.isInteger(index) || index < 0 || index >= source.length) {
        return "";
      }
      return source.charAt(index);
    }

    case "compare":
      return String(input1 ?? "").localeCompare(String(input2 ?? ""), locale, {
        sensitivity: "base",
        numeric: true,
      });

    case "join":
      return toListValue(input1).join(String(input2 ?? ","));

    case "push":
      return [...toListValue(input1), input2];

    case "remove": {
      const source = [...toListValue(input1)];
      const index = Number(input2);
      if (!Number.isInteger(index) || index < 0 || index >= source.length) {
        return source;
      }
      source.splice(index, 1);
      return source;
    }

    case "take":
      return evaluateListTake(input1, input2);

    case "get": {
      const source = toListValue(input1);
      const index = Number(input2);
      if (!Number.isInteger(index) || index < 0 || index >= source.length) {
        return undefined;
      }
      return source[index];
    }

    case "map": {
      const source = toListValue(input1);
      const path = String(input2 ?? "").trim();
      if (!path) {
        return source;
      }
      return source.map((item) => resolvePath(item, path));
    }

    case "groupBy":
      return evaluateListGroupBy(input1, input2);

    case "aggregateGroups":
      return evaluateListAggregateGroupsBy(input1, () => input2);

    case "find":
      return options?.outputKind === "index"
        ? evaluateListFindIndex(
            input1,
            input2,
            options?.objectFieldPath ?? undefined,
          )
        : evaluateListFind(
            input1,
            input2,
            options?.objectFieldPath ?? undefined,
          );

    case "findByCondition": {
      const truthy = coerceToBoolean(input2);
      if (options?.outputKind === "index") {
        return truthy ? 0 : -1;
      }
      return truthy ? toListValue(input1)[0] : undefined;
    }

    case "filter":
      return coerceToBoolean(input2) ? [...toListValue(input1)] : [];

    case "sort":
      return [...toListValue(input1)];

    case "getByPath": {
      if (input1 == null || typeof input1 !== "object") {
        return "";
      }
      const path = String(input2 ?? "").trim();
      if (!path) {
        return "";
      }
      const resolved = resolvePath(input1, path);
      return resolved == null ? "" : String(resolved);
    }

    case "withAlpha": {
      const color = String(input1 ?? "").trim();
      if (!isColorString(color)) {
        return "";
      }
      const alpha = clamp(Number(input2), 0, 1);
      const opaquePercent = Math.round((1 - alpha) * 100);
      return `color-mix(in srgb, transparent ${opaquePercent}%, ${color})`;
    }

    case "isSame":
      return (
        new Date(String(input1)).getTime() ===
        new Date(String(input2)).getTime()
      );
    case "isAfter":
      return (
        new Date(String(input1)).getTime() > new Date(String(input2)).getTime()
      );
    case "isBefore":
      return (
        new Date(String(input1)).getTime() < new Date(String(input2)).getTime()
      );
    case "addDays":
      return new Date(
        new Date(String(input1)).getTime() +
          Number(input2) * 24 * 60 * 60 * 1000,
      );
    case "addMonths":
      return new Date(
        new Date(String(input1)).getTime() +
          Number(input2) * 30 * 24 * 60 * 60 * 1000,
      );
    case "addYears":
      return new Date(
        new Date(String(input1)).getTime() +
          Number(input2) * 365 * 24 * 60 * 60 * 1000,
      );
    case "addHours":
      return new Date(
        new Date(String(input1)).getTime() + Number(input2) * 60 * 60 * 1000,
      );
    case "addMinutes":
      return new Date(
        new Date(String(input1)).getTime() + Number(input2) * 60 * 1000,
      );
    case "addSeconds":
      return new Date(
        new Date(String(input1)).getTime() + Number(input2) * 1000,
      );

    case "equals":
    case "notEquals":
      return false;

    default:
      return false;
  }
}

/**
 * Evaluates a ternary operator (aligned with {@link OperatorBehaviorNode} runtime).
 */
export function evaluateTernaryOperator(
  operator: TernaryOperator,
  input1: unknown,
  input2: unknown,
  input3: unknown,
  options?: BinaryOperatorOptions,
): unknown {
  if (operator === "setProperty")
    return setObjectProperty(input1, input2, input3);
  switch (operator) {
    case "replace":
      return String(input1 ?? "").replace(
        String(input2 ?? ""),
        String(input3 ?? ""),
      );

    case "boolToSign":
      return coerceToBoolean(input1) ? Number(input2) : Number(input3);

    case "ifElse":
      return coerceToBoolean(input3) ? input1 : input2;

    case "mix": {
      const c1 = String(input1 ?? "").trim();
      const c2 = String(input2 ?? "").trim();
      if (!isColorString(c1) || !isColorString(c2)) {
        return "";
      }
      const ratio = toColorMixRatio(input3);
      const leftPercent = Math.round(100 - ratio);
      const rightPercent = Math.round(ratio);
      return `color-mix(in srgb, ${c1} ${leftPercent}%, ${c2} ${rightPercent}%)`;
    }

    case "findByPath":
      return options?.outputKind === "index"
        ? evaluateListFindByPathIndex(input1, input2, input3)
        : evaluateListFindByPath(input1, input2, input3);

    default:
      return false;
  }
}
